import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiTool, FiShield, FiSmartphone, FiBattery, FiDroplet, FiWifi, FiHeadphones, FiMonitor } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiSmartphone,
      title: 'Mobile Phone Sales',
      description: 'Latest smartphones from all major brands',
      features: [
        'iPhone (all models)',
        'Samsung Galaxy Series',
        'Google Pixel Phones',
        'OnePlus Devices',
        'Unlocked Phones',
        'Carrier Plans Available'
      ],
      price: 'Starting from $199'
    },
    {
      icon: FiTool,
      title: 'Screen Repair',
      description: 'Professional screen replacement services',
      features: [
        'LCD & OLED Displays',
        'Genuine Parts Only',
        '30-Day Warranty',
        'Same Day Service',
        'All Phone Models',
        'Free Diagnosis'
      ],
      price: 'Starting from $89'
    },
    {
      icon: FiBattery,
      title: 'Battery Replacement',
      description: 'Restore your phone\'s battery life',
      features: [
        'High Quality Batteries',
        'Quick Replacement',
        'Performance Testing',
        '6-Month Warranty',
        'All Major Brands',
        'Battery Health Check'
      ],
      price: 'Starting from $49'
    },
    {
      icon: FiDroplet,
      title: 'Water Damage Repair',
      description: 'Advanced water damage restoration',
      features: [
        'Ultrasonic Cleaning',
        'Component Level Repair',
        'Data Recovery Attempts',
        'No Fix, No Fee',
        'Professional Assessment',
        '48-Hour Turnaround'
      ],
      price: 'Starting from $79'
    },
    {
      icon: FiShield,
      title: 'Phone Cases & Protection',
      description: 'Premium protective accessories',
      features: [
        'OtterBox Cases',
        'Tempered Glass Protectors',
        'Wireless Chargers',
        'Car Mounts',
        'Screen Cleaning Kits',
        'Custom Designs'
      ],
      price: 'Starting from $19'
    },
    {
      icon: FiWifi,
      title: 'Software & Unlocking',
      description: 'Software solutions and unlocking services',
      features: [
        'Network Unlocking',
        'Software Updates',
        'Virus Removal',
        'Data Transfer',
        'System Optimization',
        'App Installation'
      ],
      price: 'Starting from $39'
    }
  ];

  const repairProcess = [
    {
      step: '1',
      title: 'Free Diagnosis',
      description: 'Bring your device for a comprehensive free assessment'
    },
    {
      step: '2',
      title: 'Quote & Approval',
      description: 'Receive a detailed quote and approve the repair work'
    },
    {
      step: '3',
      title: 'Expert Repair',
      description: 'Our certified technicians perform the repair with genuine parts'
    },
    {
      step: '4',
      title: 'Quality Testing',
      description: 'Thorough testing to ensure everything works perfectly'
    },
    {
      step: '5',
      title: 'Warranty & Pickup',
      description: 'Collect your device with warranty and satisfaction guarantee'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-gray-100 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Our <span className="primary-red">Premium Services</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              From the latest mobile phones to expert repair services, we provide comprehensive solutions 
              for all your mobile needs with unmatched quality and customer service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-primary-red text-white w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={service.icon} className="text-2xl" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-3 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="border-t pt-4">
                  <span className="text-2xl font-bold primary-red">{service.price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Repair Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Repair Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a systematic approach to ensure your device is repaired to the highest standards 
              with complete transparency and quality assurance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {repairProcess.map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-primary-red text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{process.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Service Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Take a look at our professional repair work and the quality products we offer.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'https://i.ibb.co/Y7CYzkV6/images-13.jpg',
              'https://i.ibb.co/N6hwycq9/images-14.jpg',
              'https://i.ibb.co/1Y47xCwg/images-15.jpg',
              'https://i.ibb.co/Zz6pjGNv/images-16.jpg',
              'https://i.ibb.co/C5PvCTVX/images-17.jpg',
              'https://i.ibb.co/Gv0RkRq1/images-19.jpg',
              'https://i.ibb.co/s95jqbTC/images-20.jpg',
              'https://i.ibb.co/GQLsDQgW/images-21.jpg'
            ].map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <img
                  src={src}
                  alt={`Service ${index + 1}`}
                  className="w-full h-48 object-cover hover:opacity-90 transition-opacity duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-red text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Need Mobile Service?</h2>
            <p className="text-xl text-red-100 mb-8 leading-relaxed">
              Don't wait! Bring your device to Global Mobilemart Inc for expert service and competitive prices. 
              Our certified technicians are ready to help you today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+14374381760"
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              >
                Call Now: +14374381760
              </a>
              <a
                href="#"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-all duration-300"
              >
                Visit Our Store
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;